/**
 * Shared modal for adding a new hearing-aid serial into either the
 * SALEABLE or DEMO pool. Handles the Vendor vs Borrowed source split
 * AND the Single-Unit vs Kit (R+L pair) intake mode (2026-02-13).
 *
 * Used by:
 *   - SaleableStockPage → "+ Add to Saleable Pool"
 *   - DemoStockPage      → "+ Add Demo Unit"
 *
 * Props:
 *   pool:      'saleable' | 'demo'  (drives label, colour, POST payload)
 *   onClose:   ()  => void
 *   onDone:    ()  => void  (fires after successful save)
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Truck } from 'lucide-react';
import ModalShell from '../../components/ModalShell';
import HASpecPicker from '../../components/HASpecPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AddSerialModal({ pool = 'saleable', onClose, onDone }) {
  const isDemo = pool === 'demo';
  // 2026-02-11 · When the Demo tab opens this modal, the audiologist
  // can pick either "Vendor Demo" (keeps pool='demo', source_kind='vendor')
  // or "Loaner" (flips to pool='loaner', source_kind='borrowed', shows
  // Return By + Borrowed From). Rest of the fields stay the same.
  const [intake, setIntake] = useState(isDemo ? 'demo' : 'saleable'); // 'saleable' | 'demo' | 'loaner'
  const isLoaner = intake === 'loaner';
  const heading = isLoaner ? 'Add Loaner Unit' : (isDemo ? 'Add Demo Unit' : 'Add to Saleable Pool');
  const help = isLoaner
    ? 'Register a unit borrowed from a partner clinic. Auto-tagged with return-by and source; must be handed back.'
    : (isDemo
      ? 'Flag a fresh unit straight into the demo pool. Never sold — used only for patient trials.'
      : 'Add a unit to the pool that can be dispensed to a patient.');
  const testidPrefix = isLoaner ? 'ha-loanerAdd' : (isDemo ? 'ha-demoadd' : 'ha-saleable-add');

  const [products, setProducts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [productId, setProductId] = useState('');
  const [branchId, setBranchId] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [warrantyEnd, setWarrantyEnd] = useState('');
  const [sourceKind, setSourceKind] = useState('vendor');
  const [grnNo, setGrnNo] = useState('');
  const [borrowedFrom, setBorrowedFrom] = useState('');
  const [borrowReason, setBorrowReason] = useState('');
  const [returnBy, setReturnBy] = useState('');
  const [spec, setSpec] = useState({});
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // 2026-02-13 · Kit vs Single intake.
  // A hearing-aid SKU can be sellable as a Single Unit (1 aid, own
  // serial), as a Kit (R+L pair, one bundle price, two serials), or
  // both. When the picked SKU allows both, the audiologist picks the
  // packing mode via the "Intake mode" chip below. Kit rows are POSTed
  // together with a shared frontend-generated `kit_id` (UUID) so the
  // backend can validate exactly-2 rows per kit_id + one 'left' + one
  // 'right' side.
  const [intakeMode, setIntakeMode] = useState('single'); // 'single' | 'kit'
  const [rightSerial, setRightSerial] = useState('');
  const [leftSerial, setLeftSerial] = useState('');
  const [rightSpec, setRightSpec] = useState({});
  const [leftSpec, setLeftSpec] = useState({});
  const [kitCost, setKitCost] = useState('');

  // Loaner intake ALWAYS means borrowed — force the flags so the
  // Vendor/Borrowed sub-toggle can't disagree with the Intake chip.
  useEffect(() => {
    if (isLoaner) {
      setSourceKind('borrowed');
    }
  }, [isLoaner]);

  // Derive device family from the picked product's form_factor so
  // the picker shows the right power/length dropdowns without asking
  // the audiologist to re-pick the type.
  const pickedProduct = useMemo(
    () => products.find((p) => p.product_id === productId),
    [products, productId],
  );
  const deviceType = String(pickedProduct?.form_factor || '').toUpperCase();

  // Kit-mode gate — driven by the picked SKU's `sellable_as_*` flags.
  // Legacy SKUs (before 2026-02-13) may lack these fields on the wire;
  // fall back to (single=true, kit=false) so old catalogues keep
  // behaving exactly as they did.
  const allowsSingle = pickedProduct
    ? (pickedProduct.sellable_as_single !== false)
    : true;
  const allowsKit = pickedProduct
    ? !!pickedProduct.sellable_as_kit
    : false;
  const showIntakeToggle = allowsSingle && allowsKit;

  useEffect(() => {
    axios.get(`${API}/ha/products`).then((r) => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API}/branches`).then((r) => {
      const list = r.data || [];
      setBranches(list);
      if (list[0]?.branch_id) setBranchId(list[0].branch_id);
    }).catch(() => {});
  }, []);

  // Auto-adjust intake mode when the picked SKU allows only one
  // packing mode. Prevents an audiologist from picking a kit-only SKU
  // then trying to add a single unit (or vice-versa).
  useEffect(() => {
    if (!pickedProduct) return;
    if (!allowsSingle && allowsKit) setIntakeMode('kit');
    else if (allowsSingle && !allowsKit) setIntakeMode('single');
  }, [pickedProduct, allowsSingle, allowsKit]);

  const save = async () => {
    setErr('');
    if (!productId) { setErr('Pick a product from the catalogue'); return; }
    if (intakeMode === 'single') {
      if (!serialNo.trim()) { setErr('Serial number is required'); return; }
    } else {
      if (!allowsKit) {
        setErr('This SKU is not sellable as a Kit — edit it in the Catalogue to enable Kit intake.');
        return;
      }
      if (!rightSerial.trim() || !leftSerial.trim()) {
        setErr('Kit intake needs BOTH Right and Left serial numbers.');
        return;
      }
      if (rightSerial.trim() === leftSerial.trim()) {
        setErr('Right and Left serial numbers must be different.');
        return;
      }
    }
    if ((sourceKind === 'borrowed' || isLoaner) && !borrowedFrom.trim()) {
      setErr('Borrowed units need a "Borrowed from" (source clinic)');
      return;
    }
    setSaving(true);
    // Resolve the destination pool from the Intake chip so a Demo-tab
    // audiologist can register a Loaner unit without leaving the modal.
    const targetPool = isLoaner ? 'loaner' : (isDemo ? 'demo' : 'saleable');
    const effectiveSourceKind = isLoaner ? 'borrowed' : sourceKind;
    const baseRow = {
      branch_id: branchId,
      pool: targetPool,
      warranty_end_date: warrantyEnd || null,
      grn_no: effectiveSourceKind === 'vendor' ? (grnNo.trim() || null) : null,
      source_kind: effectiveSourceKind,
      borrowed_from: effectiveSourceKind === 'borrowed' ? borrowedFrom.trim() : null,
      borrow_reason: effectiveSourceKind === 'borrowed' ? (borrowReason.trim() || null) : null,
      return_by_date: (effectiveSourceKind === 'borrowed' && returnBy) ? returnBy : null,
    };
    let rows;
    if (intakeMode === 'single') {
      rows = [{
        ...baseRow,
        serial_no: serialNo.trim().toUpperCase(),
        spec: spec && Object.keys(spec).length ? spec : null,
      }];
    } else {
      // Kit intake — two rows share a UUID so the sale/dispense flow
      // can atomically lift the pair. `kit_side` distinguishes ears;
      // `kit_cost` is optional and stamped on BOTH rows for audit
      // (analytics still count one kit, not two singles — the sale
      // side reads by kit_id and quotes ONE bundle price).
      const kitId =
        (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `kit-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const kitCostNum = kitCost.trim() ? Number(kitCost) : null;
      rows = [
        {
          ...baseRow,
          serial_no: rightSerial.trim().toUpperCase(),
          kit_id: kitId,
          kit_side: 'right',
          kit_cost: kitCostNum,
          spec: (rightSpec && Object.keys(rightSpec).length) ? rightSpec : null,
        },
        {
          ...baseRow,
          serial_no: leftSerial.trim().toUpperCase(),
          kit_id: kitId,
          kit_side: 'left',
          kit_cost: kitCostNum,
          spec: (leftSpec && Object.keys(leftSpec).length) ? leftSpec : null,
        },
      ];
    }
    try {
      await axios.post(`${API}/ha/products/${productId}/serials`, rows);
      onDone();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === 'string' ? d : (Array.isArray(d) ? d[0]?.msg : 'Save failed'));
    } finally { setSaving(false); }
  };

  const btnPrimary = isLoaner
    ? 'bg-amber-600 hover:bg-amber-700'
    : (isDemo ? 'bg-purple-600 hover:bg-purple-700' : 'bg-indigo-600 hover:bg-indigo-700');
  const toggleActive = isLoaner
    ? 'bg-amber-600 text-white border-amber-600'
    : (isDemo ? 'bg-purple-600 text-white border-purple-600' : 'bg-indigo-600 text-white border-indigo-600');
  const iconColor = isLoaner ? 'text-amber-600' : (isDemo ? 'text-purple-600' : 'text-indigo-600');

  return (
    <ModalShell onClose={onClose} cardClassName="max-w-lg w-full p-5 max-h-[92vh] overflow-auto" testid={`${testidPrefix}-modal`}>
      <h2 className="text-base font-bold mb-1 flex items-center gap-2">
        <Truck size={16} className={iconColor} /> {heading}
      </h2>
      <p className="text-[11px] text-slate-500 mb-3">{help}</p>

      {/* Intake type — only shown on the Demo tab. Lets the same modal
          register a plain demo unit OR a loaner (borrowed) unit
          without opening a different flow. Saleable tab keeps its
          existing Vendor / Borrowed sub-toggle for backwards-compat. */}
      {isDemo && (
        <div className="mb-3">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">Intake type</label>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid={`${testidPrefix}-intake-chip`}>
            {[
              { k: 'demo',   lab: 'Vendor Demo',        hint: 'Trial unit purchased from a vendor.' },
              { k: 'loaner', lab: 'Loaner (borrowed)',  hint: 'Borrowed from a partner clinic — must be returned.' },
            ].map(({ k, lab, hint }) => {
              const active = intake === k;
              const cls = active
                ? (k === 'loaner'
                    ? 'border-amber-500 bg-amber-50 text-amber-900'
                    : 'border-purple-500 bg-purple-50 text-purple-900')
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50';
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setIntake(k)}
                  data-testid={`${testidPrefix}-intake-${k}`}
                  className={`text-left px-3 py-2 border rounded-md transition-colors ${cls}`}
                >
                  <div className="text-xs font-semibold">{lab}</div>
                  <div className="text-[10px] leading-tight opacity-80">{hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <label className="block text-[11px] font-semibold text-slate-600 mb-1">Product *</label>
      <select
        value={productId}
        onChange={(e) => setProductId(e.target.value)}
        data-testid={`${testidPrefix}-product`}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-2"
      >
        <option value="">— pick a catalogue SKU —</option>
        {products.map((p) => (
          <option key={p.product_id} value={p.product_id}>
            {p.brand} · {p.model} ({p.form_factor})
          </option>
        ))}
      </select>

      {/* Picked SKU details — reassure the audiologist they've chosen
          the right catalogue row before filling the serial number.
          Reads brand / model / form-factor + default colour · power ·
          size straight off the picked product, so future edits to the
          catalogue reflect here immediately. */}
      {pickedProduct && (
        <div
          className="mb-3 -mt-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px]"
          data-testid={`${testidPrefix}-picked-details`}
        >
          <div className="grid grid-cols-3 gap-2 gap-y-1">
            <div><span className="text-slate-500">Make</span><div className="font-semibold text-slate-800">{pickedProduct.brand || '—'}</div></div>
            <div><span className="text-slate-500">Model</span><div className="font-semibold text-slate-800">{pickedProduct.model || '—'}</div></div>
            <div><span className="text-slate-500">Type</span><div className="font-semibold uppercase tracking-wider text-slate-800">{pickedProduct.form_factor || '—'}</div></div>
            <div><span className="text-slate-500">Colour</span><div className="font-medium text-slate-700">{pickedProduct.default_color || '—'}</div></div>
            <div><span className="text-slate-500">Power</span><div className="font-medium text-slate-700">{pickedProduct.default_power || '—'}</div></div>
            <div><span className="text-slate-500">Size</span><div className="font-medium text-slate-700">{pickedProduct.default_size || '—'}</div></div>
          </div>
        </div>
      )}

      {/* 2026-02-13 · Intake mode toggle — only rendered when the picked
          SKU is sellable as BOTH Single Unit and Kit. Kit-only or
          Single-only SKUs auto-lock via the useEffect above; a
          contextual notice is shown for kit-only SKUs so the
          audiologist understands why the two-serial layout appeared
          without a toggle. */}
      {pickedProduct && showIntakeToggle && (
        <div className="mb-3">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">Intake mode</label>
          <div className="grid grid-cols-2 gap-2" role="radiogroup" data-testid={`${testidPrefix}-intake-mode`}>
            {[
              { k: 'single', lab: 'Single Unit',        hint: 'One aid · one serial number.' },
              { k: 'kit',    lab: 'Kit (Right + Left)', hint: 'Two aids · one kit price · two serials.' },
            ].map(({ k, lab, hint }) => {
              const active = intakeMode === k;
              const cls = active
                ? toggleActive
                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50';
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setIntakeMode(k)}
                  data-testid={`${testidPrefix}-intake-mode-${k}`}
                  className={`text-left px-3 py-2 border rounded-md transition-colors ${cls}`}
                >
                  <div className="text-xs font-semibold">{lab}</div>
                  <div className="text-[10px] leading-tight opacity-80">{hint}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {pickedProduct && !showIntakeToggle && intakeMode === 'kit' && (
        <div
          className="mb-3 rounded px-2 py-1.5 bg-indigo-50 text-indigo-800 text-[11px] border border-indigo-200"
          data-testid={`${testidPrefix}-kit-only-notice`}
        >
          This SKU is sold as a Kit only — capture both Right and Left serials below.
        </div>
      )}

      {/* Serial number capture — flips between one field (Single) and
          two side-labelled fields (Kit). Branch is shared across both
          in Kit mode. */}
      {intakeMode === 'single' ? (
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Serial No. *</label>
            <input
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
              data-testid={`${testidPrefix}-serial`}
              placeholder="Manufacturer sticker"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Branch</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            >
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      ) : (
        <div
          className="mb-3 space-y-2 p-3 rounded border border-indigo-200 bg-indigo-50/40"
          data-testid={`${testidPrefix}-kit-block`}
        >
          <div className="text-[11px] font-semibold text-indigo-900">
            Kit — capture both aids
            <span className="ml-1 font-normal italic text-indigo-700/80">
              · one kit price · two serial numbers
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-[10.5px] font-semibold text-indigo-900 mb-1">
                Right ear · Serial No. *
              </label>
              <input
                value={rightSerial}
                onChange={(e) => setRightSerial(e.target.value)}
                data-testid={`${testidPrefix}-kit-right-serial`}
                placeholder="Manufacturer sticker · R"
                className="w-full border border-indigo-300 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
            <div>
              <label className="block text-[10.5px] font-semibold text-indigo-900 mb-1">
                Left ear · Serial No. *
              </label>
              <input
                value={leftSerial}
                onChange={(e) => setLeftSerial(e.target.value)}
                data-testid={`${testidPrefix}-kit-left-serial`}
                placeholder="Manufacturer sticker · L"
                className="w-full border border-indigo-300 rounded px-2 py-1.5 text-sm bg-white"
              />
            </div>
          </div>
          <div>
            <label className="block text-[10.5px] font-semibold text-indigo-900 mb-1">Branch</label>
            <select
              value={branchId}
              onChange={(e) => setBranchId(e.target.value)}
              data-testid={`${testidPrefix}-kit-branch`}
              className="w-full border border-indigo-300 rounded px-2 py-1.5 text-sm bg-white"
            >
              {branches.map((b) => (
                <option key={b.branch_id} value={b.branch_id}>{b.name}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Source toggle — only shown when the Intake type isn't already
          Loaner. Loaner intake implies borrowed, so we hide the
          toggle and pin source_kind='borrowed'. Saleable pool keeps
          the full Vendor / Borrowed sub-toggle so an audiologist can
          register a borrowed-into-saleable unit (rare but supported).*/}
      {!isLoaner && (
        <div className="mb-3">
          <label className="block text-[11px] font-semibold text-slate-600 mb-1">Source</label>
          <div className="flex gap-2">
            {[['vendor', 'Vendor PO'], ['borrowed', 'Borrowed from another clinic']].map(([k, lab]) => (
              <button
                key={k}
                type="button"
                onClick={() => setSourceKind(k)}
                data-testid={`${testidPrefix}-source-${k}`}
                className={`flex-1 px-3 py-1.5 text-[12px] font-semibold rounded border ${
                  sourceKind === k ? toggleActive : 'bg-white text-slate-700 border-slate-300'
                }`}
              >{lab}</button>
            ))}
          </div>
        </div>
      )}

      {sourceKind === 'vendor' && !isLoaner ? (
        <div className={`grid ${intakeMode === 'kit' ? 'grid-cols-3' : 'grid-cols-2'} gap-3 mb-3`}>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">GRN No.</label>
            <input
              value={grnNo}
              onChange={(e) => setGrnNo(e.target.value)}
              data-testid={`${testidPrefix}-grn`}
              placeholder="Optional"
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-1">Warranty end</label>
            <input
              type="date"
              value={warrantyEnd}
              onChange={(e) => setWarrantyEnd(e.target.value)}
              className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
          </div>
          {intakeMode === 'kit' && (
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">Kit Cost (₹)</label>
              <input
                type="number"
                value={kitCost}
                onChange={(e) => setKitCost(e.target.value)}
                data-testid={`${testidPrefix}-kit-cost`}
                placeholder="Optional"
                className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
            </div>
          )}
        </div>
      ) : (
        <div className={`mb-3 space-y-2 p-3 rounded border ${isLoaner ? 'border-amber-200 bg-amber-50' : 'border-rose-200 bg-rose-50'}`}>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={`block text-[11px] font-semibold ${isLoaner ? 'text-amber-900' : 'text-slate-700'} mb-1`}>Borrowed from *</label>
              <input
                value={borrowedFrom}
                onChange={(e) => setBorrowedFrom(e.target.value)}
                data-testid={`${testidPrefix}-borrowed-from`}
                placeholder="e.g., ABC Speech & Hearing"
                className={`w-full border ${isLoaner ? 'border-amber-300' : 'border-rose-300'} rounded px-2 py-1.5 text-sm bg-white`}
              />
            </div>
            <div>
              <label className={`block text-[11px] font-semibold ${isLoaner ? 'text-amber-900' : 'text-slate-700'} mb-1`}>Return by <span className="lowercase font-normal italic opacity-70">· recommended</span></label>
              <input
                type="date"
                value={returnBy}
                onChange={(e) => setReturnBy(e.target.value)}
                data-testid={`${testidPrefix}-return-by`}
                className={`w-full border ${isLoaner ? 'border-amber-300' : 'border-rose-300'} rounded px-2 py-1.5 text-sm bg-white`}
              />
            </div>
          </div>
          <div>
            <label className={`block text-[11px] font-semibold ${isLoaner ? 'text-amber-900' : 'text-slate-700'} mb-1`}>Reason (recommended)</label>
            <textarea
              value={borrowReason}
              onChange={(e) => setBorrowReason(e.target.value)}
              data-testid={`${testidPrefix}-borrow-reason`}
              placeholder="Why did we borrow this unit? (patient trial urgent, out of stock, etc.)"
              rows={2}
              className={`w-full border ${isLoaner ? 'border-amber-300' : 'border-rose-300'} rounded px-2 py-1.5 text-sm bg-white`}
            />
          </div>
          {intakeMode === 'kit' && (
            <div>
              <label className={`block text-[11px] font-semibold ${isLoaner ? 'text-amber-900' : 'text-slate-700'} mb-1`}>Kit Cost (₹) <span className="lowercase font-normal italic opacity-70">· optional</span></label>
              <input
                type="number"
                value={kitCost}
                onChange={(e) => setKitCost(e.target.value)}
                data-testid={`${testidPrefix}-kit-cost`}
                placeholder="Optional"
                className={`w-full border ${isLoaner ? 'border-amber-300' : 'border-rose-300'} rounded px-2 py-1.5 text-sm bg-white`}
              />
            </div>
          )}
        </div>
      )}

      {err && <div className="bg-rose-50 text-rose-700 text-xs p-2 rounded mb-2">{err}</div>}

      {/* Device spec — colour + power + wire/tube length. In Single
          mode we show one picker (side inferred at fitting time). In
          Kit mode we show two pickers — Right and Left — because a
          real-world Kit box can ship with different colours or
          receiver powers per ear. */}
      {productId && intakeMode === 'single' && (
        <div className="mb-3 p-3 rounded border border-slate-200 bg-slate-50">
          <HASpecPicker
            deviceType={deviceType}
            side="R"                /* single-unit intake — side is
                                       recorded at fitting time */
            value={spec}
            onChange={setSpec}
            testIdPrefix={`${testidPrefix}-spec`}
            title="Device specification (at intake)"
            compact
          />
        </div>
      )}
      {productId && intakeMode === 'kit' && (
        <div className="mb-3 space-y-2">
          <div className="p-3 rounded border border-indigo-200 bg-indigo-50/40">
            <HASpecPicker
              deviceType={deviceType}
              side="R"
              value={rightSpec}
              onChange={setRightSpec}
              testIdPrefix={`${testidPrefix}-kit-right-spec`}
              title="Right ear · device specification"
              compact
            />
          </div>
          <div className="p-3 rounded border border-indigo-200 bg-indigo-50/40">
            <HASpecPicker
              deviceType={deviceType}
              side="L"
              value={leftSpec}
              onChange={setLeftSpec}
              testIdPrefix={`${testidPrefix}-kit-left-spec`}
              title="Left ear · device specification"
              compact
            />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} disabled={saving}
                className="px-3 py-1.5 text-xs font-semibold border border-slate-300 rounded">Cancel</button>
        <button
          onClick={save}
          disabled={saving}
          data-testid={`${testidPrefix}-save`}
          className={`px-4 py-1.5 text-xs font-semibold text-white rounded disabled:opacity-50 ${btnPrimary}`}
        >{saving ? 'Saving…' : (
          intakeMode === 'kit'
            ? (isLoaner ? 'Add Loaner Kit' : (isDemo ? 'Add Kit to Demo Pool' : 'Add Kit to Pool'))
            : (isLoaner ? 'Add Loaner Unit' : (isDemo ? 'Add to Demo Pool' : 'Add to pool'))
        )}</button>
      </div>
    </ModalShell>
  );
}
